/*
 * Öffentliche Texte von schuetzenbuch.de: Startseite, Impressum, Datenschutz.
 *
 * Diese Datei wird von der App (Vue) UND vom Build-Skript scripts/prerender-public.js
 * gelesen. Deshalb: CommonJS, nur Daten, keine Imports.
 * In Fließtexten ist einfaches Inline-HTML erlaubt (<a>, <strong>, <br>).
 * Überschriften (title, q) bitte ohne HTML.
 *
 * Alle Texte als Markdown exportieren (z. B. für NotebookLM):  npm run texte
 */

const CONTACT_MAIL = 'mail@schuetzenbuch.de';
const mailLink = `<a href="mailto:${CONTACT_MAIL}">${CONTACT_MAIL}</a>`;
const extLink = (href, text) => `<a href="${href}" target="_blank" rel="noopener">${text}</a>`;

const site = {
  name: 'schuetzenbuch.de',
  url: 'https://schuetzenbuch.de',
  contactMail: CONTACT_MAIL,
  description:
    'Das digitale Schießbuch für Sportschützen und Vereine: Trainings erfassen, vom Verein bestätigen lassen, Waffen und Arbeitsstunden verwalten.'
};

const landing = {
  path: '/',
  seo: {
    path: '/',
    title: 'Digitales Schießbuch für Sportschützen und Vereine | schuetzenbuch.de',
    description:
      'Trainings erfassen, vom Verein bestätigen lassen und als PDF exportieren. Dazu Waffenverwaltung, Standaufsicht und Arbeitsstunden. Das digitale Schießbuch aus Mainz.'
  },

  hero: {
    kicker: 'Für Sportschützen und Schützenvereine',
    title: 'Das digitale Schießbuch für Sportschützen und Vereine',
    lead:
      'Trainings eintragen, vom Verein bestätigen lassen und beim nächsten Bedürfnisnachweis einfach ausdrucken. Dazu deine Waffen, die Standaufsicht und die Arbeitsstunden im Verein. Alles an einem Ort, statt verteilt auf Heft, Ordner und Excel-Liste.',
    primary: { text: 'Jetzt registrieren', to: '/register' },
    secondary: { text: 'Anmelden', to: '/login' },
    note: 'Läuft im Browser, am PC genauso wie auf dem Smartphone.'
  },

  intro: {
    title: 'Schluss mit dem Blättern vor dem Behördentermin',
    text:
      'Wer als Sportschütze Waffen besitzt, muss belegen können, dass er regelmäßig trainiert. In vielen Vereinen heißt das bis heute: ein Heft am Stand, eine Unterschrift der Aufsicht und vor dem Termin bei der Waffenbehörde das große Suchen. schuetzenbuch.de macht daraus eine Liste, die mit jedem Training wächst, vom Verein bestätigt ist und sich jederzeit exportieren lässt.'
  },

  features: [
    {
      id: 'schiessbuch',
      mockup: 'shootingbook',
      kicker: 'Schießbuch',
      title: 'Jedes Training sauber dokumentiert',
      text:
        'Ob am eigenen Stand oder beim Wettkampf im Nachbarverein: Du trägst Datum, Stand, Waffe, Disziplin und Schusszahl ein, dein Verein bestätigt den Eintrag. Fragt die Behörde nach deinem Bedürfnis, exportierst du die Liste als PDF oder Excel.',
      bullets: [
        'Einträge im eigenen Verein und bei anderen Vereinen',
        'Bestätigung durch den Verein, auf Wunsch mit Unterschrift',
        'Export als PDF und Excel'
      ]
    },
    {
      id: 'waffen',
      mockup: 'weapons',
      kicker: 'Waffen',
      title: 'Deine Waffen und dein Bedürfnis im Blick',
      text:
        'Hersteller, Kaliber, WBK-Nummer und NWR-IDs sind hinterlegt, jede Waffe ist ihrem Bedürfnis zugeordnet. Die Übersicht zu § 14 WaffG zeigt dir, wie es um die Nachweise für deine prüfpflichtigen Sportwaffen steht. Überlässt du eine Waffe, wird das mit Protokoll festgehalten.',
      bullets: [
        'Waffen mit WBK-Nummer, NWR-Waffen-ID und Bedürfnis',
        'Übersicht nach § 14 WaffG',
        'Überlassungen mit PDF-Protokoll'
      ]
    },
    {
      id: 'verein',
      mockup: 'today',
      kicker: 'Am Stand',
      title: 'Jeder sieht, wer gerade da ist',
      text:
        'Die Ansicht „Heute“ zeigt Schießleiter, Standaufsicht und alle, die gerade schießen, auf Wunsch auch als Monitor im Vereinsheim. Am Terminal buchen sich Mitglieder mit ihrer RFID-Karte ein und aus, die Einträge landen direkt im Schießbuch.',
      bullets: [
        'Schießleiter und Standaufsicht auf einen Blick',
        'Monitor-Ansicht für das Vereinsheim',
        'Terminal mit RFID-Karten am Schießstand'
      ]
    },
    {
      id: 'arbeitsstunden',
      mockup: 'workinghours',
      kicker: 'Arbeitsstunden',
      title: 'Arbeitsstunden ohne Zettel am Schwarzen Brett',
      text:
        'Mitglieder tragen ihre Stunden selbst ein, der Vorstand gibt sie frei. Arbeitseinsätze lassen sich planen und per E-Mail ankündigen, die Soll-Stunden hängen an der Beitragsgruppe. Am Jahresende steht die Auswertung fertig da.',
      bullets: [
        'Erfassung durch die Mitglieder, Freigabe durch den Verein',
        'Arbeitseinsätze mit Einladung per E-Mail',
        'Soll-Stunden je Beitragsgruppe und Jahresauswertung'
      ]
    }
  ],

  audiences: [
    {
      id: 'schuetzen',
      icon: 'mdi-target-account',
      title: 'Für Schützinnen und Schützen',
      items: [
        'Schießbuch auf dem Smartphone, direkt am Stand ausgefüllt',
        'Einträge aus anderen Vereinen und von Wettkämpfen',
        'Waffen mit WBK-Nummer, NWR-IDs und Bedürfnis',
        'Ausbildungen und Berechtigungen, zum Beispiel als Standaufsicht',
        'Auswertung deiner Trainings über beliebige Zeiträume'
      ]
    },
    {
      id: 'vereine',
      icon: 'mdi-home-group',
      title: 'Für Vereine',
      items: [
        'Mitglieder, Stände und Bahnen verwalten',
        'Schießbuch-Einträge prüfen und genehmigen',
        'Heute-Ansicht mit Schießleiter und Standaufsicht',
        'Vereinswaffen verleihen und die Nutzung auswerten',
        'Arbeitsstunden, Arbeitseinsätze und Soll-Stunden'
      ]
    }
  ],

  trust: {
    title: 'Datenschutz ohne Kleingedrucktes',
    items: [
      // PRÜFEN: Rechenzentrumsstandort der IONOS-Cloud (de/fra, de/txl …) bestätigen.
      {
        icon: 'mdi-server-security',
        title: 'Server in Deutschland',
        text: 'Die Anwendung läuft bei IONOS in deutschen Rechenzentren.'
      },
      {
        icon: 'mdi-eye-off-outline',
        title: 'Kein Tracking',
        text: 'Keine Analyse-Tools, keine Werbung, kein Verkauf von Daten.'
      },
      {
        icon: 'mdi-lock-outline',
        title: 'Verschlüsselt übertragen',
        text: 'Die Verbindung läuft durchgehend über HTTPS, Passwörter speichern wir nur als Hash.'
      },
      {
        icon: 'mdi-map-marker-outline',
        title: 'Aus Mainz',
        text: 'Entwickelt und betrieben von Matthias Aßmann. Du hast einen direkten Ansprechpartner.'
      }
    ]
  },

  faqTitle: 'Häufige Fragen',
  faq: [
    {
      q: 'Ersetzt das digitale Schießbuch mein Heft?',
      a:
        'Das Schießbuch hält deine Trainings fest und lässt sie vom Verein bestätigen. Welche Form von Nachweis deine Waffenbehörde oder dein Verband verlangt, ist aber nicht überall gleich. Frag im Zweifel dort nach. Mit dem PDF-Export hast du jederzeit einen Ausdruck zur Hand.'
    },
    {
      q: 'Was brauche ich für die Registrierung?',
      a:
        'Als Schütze deine E-Mail-Adresse, deinen Namen und deine Mitgliedsnummer. Vereine registrieren sich mit Vereinsname, E-Mail-Adresse und Vereinsnummer.'
    },
    {
      q: 'Muss mein Verein auch mitmachen?',
      a:
        'Trainings bei anderen Vereinen oder auf Wettkämpfen kannst du als externe Einträge erfassen. Bestätigen kann ein Verein deine Einträge aber nur, wenn er selbst auf schuetzenbuch.de angemeldet ist.'
    },
    {
      q: 'Funktioniert das auch auf dem Handy?',
      a:
        'Ja. schuetzenbuch.de läuft im Browser, eine App musst du nicht installieren. Wenn du die Seite zum Startbildschirm hinzufügst, ist sie mit einem Tipp geöffnet.'
    },
    {
      q: 'Wo liegen meine Daten?',
      a:
        'Auf Servern von IONOS in Deutschland. Welche Daten wir speichern und welche Dienste wir einsetzen, steht ausführlich in der <a href="/datenschutz">Datenschutzerklärung</a>.'
    }
  ],

  cta: {
    title: 'Probier es beim nächsten Training aus',
    text: 'Die Registrierung dauert nur ein paar Minuten. Du brauchst deine E-Mail-Adresse und deine Mitgliedsnummer.',
    primary: { text: 'Jetzt registrieren', to: '/register' }
  },

  mockupCaption: 'Beispielansicht mit Fantasiedaten'
};

const impressum = {
  path: '/impressum',
  seo: {
    path: '/impressum',
    title: 'Impressum | schuetzenbuch.de',
    description: 'Anbieterkennzeichnung von schuetzenbuch.de, dem digitalen Schießbuch für Sportschützen und Vereine.'
  },
  title: 'Impressum',
  updated: 'September 2026',
  sections: [
    {
      id: 'anbieter',
      title: 'Angaben gemäß § 5 DDG',
      body: ['Matthias Aßmann<br>Dijonstraße 63<br>55122 Mainz<br>Deutschland']
    },
    {
      id: 'kontakt',
      title: 'Kontakt',
      body: [`E-Mail: ${mailLink}`]
    },
    {
      id: 'verantwortlich',
      title: 'Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV',
      body: ['Matthias Aßmann, Anschrift wie oben']
    },
    {
      id: 'streitbeilegung',
      title: 'Verbraucherstreitbeilegung',
      body: [
        'Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.'
      ]
    },
    {
      id: 'haftung-inhalte',
      title: 'Haftung für Inhalte',
      body: [
        'Die Inhalte dieser Seiten haben wir mit Sorgfalt erstellt. Für Richtigkeit, Vollständigkeit und Aktualität können wir trotzdem keine Gewähr übernehmen. schuetzenbuch.de ist ein Werkzeug zur Dokumentation und ersetzt keine Rechtsberatung. Welche Nachweise im Waffenrecht gelten, legen deine Waffenbehörde und dein Verband fest.'
      ]
    },
    {
      id: 'haftung-links',
      title: 'Haftung für Links',
      body: [
        'Unser Angebot enthält Links zu Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Für diese Inhalte ist der jeweilige Anbieter verantwortlich. Werden uns Rechtsverletzungen bekannt, entfernen wir solche Links umgehend.'
      ]
    }
  ]
};

const datenschutz = {
  path: '/datenschutz',
  seo: {
    path: '/datenschutz',
    title: 'Datenschutzerklärung | schuetzenbuch.de',
    description:
      'Welche Daten schuetzenbuch.de verarbeitet, welche Dienste dabei im Einsatz sind und welche Rechte du hast.'
  },
  title: 'Datenschutzerklärung',
  intro:
    'Hier steht, welche Daten schuetzenbuch.de verarbeitet, wofür wir sie brauchen und wer sie zu sehen bekommt. Wir beschreiben das so konkret wie möglich, statt nur Paragrafen aufzuzählen.',
  updated: 'September 2026',
  sections: [
    {
      id: 'verantwortlich',
      title: 'Verantwortlich',
      body: [
        `Matthias Aßmann<br>Dijonstraße 63<br>55122 Mainz<br>E-Mail: ${mailLink}`,
        'Einen Datenschutzbeauftragten müssen wir nicht benennen. Bei Fragen zum Datenschutz schreib uns einfach an die oben genannte Adresse.'
      ]
    },
    {
      id: 'ueberblick',
      title: 'Das Wichtigste in Kürze',
      body: [
        {
          list: [
            'Wir verarbeiten die Daten, die du oder dein Verein in schuetzenbuch.de eintragen, und die Daten, die technisch für den Betrieb nötig sind.',
            'Es gibt keine Analyse-Tools, keine Werbe-Tracker und keinen Verkauf von Daten.',
            'Die Anwendung läuft auf Servern von IONOS in Deutschland.',
            'Einige Funktionen nutzen externe Dienste: Fehleranalyse, Echtzeit-Aktualisierung und Spam-Schutz bei der Registrierung. Welche das sind, steht weiter unten.'
          ]
        }
      ]
    },
    {
      id: 'hosting',
      title: 'Hosting und Server-Logfiles',
      body: [
        // PRÜFEN: Standort der IONOS-Rechenzentren und abgeschlossenen AV-Vertrag bestätigen.
        'schuetzenbuch.de wird bei der IONOS SE, Elgendorfer Str. 57, 56410 Montabaur, betrieben. Webserver und Datenbanken laufen in Rechenzentren in Deutschland. Mit IONOS besteht ein Vertrag zur Auftragsverarbeitung nach Art. 28 DSGVO.',
        // PRÜFEN: konkrete Aufbewahrungsdauer der Logs (Traefik/Docker) ergänzen, z. B. „nach 14 Tagen“.
        'Wenn du die Seite aufrufst, überträgt dein Browser automatisch technische Informationen: IP-Adresse, Datum und Uhrzeit, die aufgerufene Adresse, die zuvor besuchte Seite sowie Browser und Betriebssystem. Das brauchen wir, um die Seite auszuliefern und Angriffe zu erkennen. Rechtsgrundlage ist unser berechtigtes Interesse an einem sicheren Betrieb (Art. 6 Abs. 1 lit. f DSGVO). Die Logfiles führen wir nicht mit anderen Daten zusammen und bewahren sie nur so lange auf, wie es dafür nötig ist.',
        'Die Verbindung zu schuetzenbuch.de ist durchgehend per TLS (HTTPS) verschlüsselt. Passwörter speichern wir nicht im Klartext, sondern nur als Hash.'
      ]
    },
    {
      id: 'konto',
      title: 'Registrierung und Benutzerkonto',
      body: [
        'Für ein Konto als Schütze brauchen wir deine E-Mail-Adresse, Vor- und Nachnamen, deine Mitgliedsnummer und ein Passwort. Im Profil kannst du weitere Angaben hinterlegen: Anschrift, Telefon- und Mobilnummer, IPSC-Nummer, NWR-Personen-ID und deinen Hauptverein.',
        'Für ein Vereinskonto speichern wir Vereinsname, Vereinsnummer und E-Mail-Adresse, auf Wunsch auch Anschrift und Telefonnummer.',
        'Außerdem speichern wir, wann du dich zuletzt an- und abgemeldet hast. Rechtsgrundlage ist die Nutzung von schuetzenbuch.de, also die Erfüllung des Nutzungsverhältnisses (Art. 6 Abs. 1 lit. b DSGVO).'
      ]
    },
    {
      id: 'nachweise',
      title: 'Schießbuch, Waffen und Ausbildungen',
      body: [
        'Das ist der Kern der Anwendung. Was du hier einträgst, speichern wir, damit du deine Trainings und Waffen verwalten und nachweisen kannst (Art. 6 Abs. 1 lit. b DSGVO):',
        {
          list: [
            '<strong>Schießbuch:</strong> Datum, Beginn und Ende, Verein, Stand und Bahn, Disziplin, Waffe, Art des Trainings, deine Funktion (zum Beispiel Standaufsicht), Schusszahl und Ergebnisse, bei externen Einträgen den Namen des Vereins sowie den Status der Bestätigung und wer sie wann erteilt hat.',
            '<strong>Unterschriften:</strong> Wird ein Eintrag per Unterschrift bestätigt, speichern wir die Unterschrift als Bild zum Eintrag.',
            '<strong>Waffen:</strong> Hersteller, Seriennummer, Kaliber, Waffengattung, Kurz- und Langbezeichnung, Erwerbsdatum, WBK-Nummer, NWR-Waffen-ID, NWR-Erlaubnis-ID und das zugeordnete Bedürfnis.',
            '<strong>Überlassungen:</strong> Überlässt du eine Waffe an eine andere Person, speichern wir die Angaben, die du zu ihr einträgst (Name, Vorname, Anschrift), und bis wann die Überlassung gilt. Bitte sag der Person Bescheid.',
            '<strong>Ausbildungen:</strong> Qualifikationen wie die Standaufsicht und seit wann du sie hast.'
          ]
        }
      ]
    },
    {
      id: 'verein',
      title: 'Was dein Verein sieht',
      body: [
        'Wenn du dich einem Verein zuordnest, kann dieser Verein die Daten einsehen, die er für die Vereinsarbeit braucht: deinen Namen, deine Mitgliedsnummer, deine Rolle im Verein (zum Beispiel Standaufsicht oder Schießleiter), die Schießbuch-Einträge, die du bei diesem Verein machst oder ihm zur Bestätigung schickst, deine Arbeitsstunden und deine Beitragsgruppe.',
        'In der Ansicht „Heute“ sieht der Verein, wer gerade am Stand eingebucht ist. Vereine können diese Ansicht auf einem Bildschirm im Vereinsheim zeigen. Dort sind die Namen der Anwesenden dann für alle im Raum sichtbar.',
        // PRÜFEN: Gibt es AV-Verträge mit den Vereinen? Wenn nicht, diesen Absatz anpassen.
        'Für die Daten, die ein Verein über seine Mitglieder in schuetzenbuch.de verwaltet, ist der Verein selbst verantwortlich. Wir verarbeiten diese Daten in seinem Auftrag (Art. 28 DSGVO). Fragen dazu, etwa zur Löschung, richtest du am besten zuerst an deinen Verein.'
      ]
    },
    {
      id: 'terminal',
      title: 'Terminal und RFID-Karten am Schießstand',
      body: [
        'Manche Vereine betreiben am Schießstand ein Terminal, an dem du dich mit einer RFID-Karte oder einem Transponder ein- und ausbuchst. Dafür speichern wir die Kennung deiner Karte und ihre Zuordnung zu deinem Konto und deinem Verein. Jede Buchung am Terminal protokollieren wir mit Zeitpunkt und Terminal, damit sich die Einträge im Schießbuch nachvollziehen lassen (Art. 6 Abs. 1 lit. b DSGVO).',
        'Die Terminals melden außerdem technische Zustandsdaten wie Prozessorauslastung, Arbeitsspeicher und Temperatur. Diese Daten beziehen sich nur auf das Gerät, nicht auf Personen.'
      ]
    },
    {
      id: 'arbeitsstunden',
      title: 'Arbeitsstunden',
      body: [
        'Trägst du Arbeitsstunden ein, speichern wir Verein, Tätigkeit, Beginn und Ende und, falls vorhanden, den zugehörigen Arbeitseinsatz. Dazu kommen der Status der Freigabe und gegebenenfalls der Grund einer Ablehnung. Lädt dein Verein dich zu einem Arbeitseinsatz ein, speichern wir die Einladung, deine Antwort und einen persönlichen Link, der nach einer Weile ungültig wird (Art. 6 Abs. 1 lit. b DSGVO).'
      ]
    },
    {
      id: 'emails',
      title: 'E-Mails',
      body: [
        // PRÜFEN: Über welchen Dienst läuft der SMTP-Versand (MAIL_HOST=smtp)? Externen Anbieter hier nennen.
        'Wir schicken dir E-Mails, die zur Nutzung gehören, zum Beispiel zur Bestätigung deiner Adresse, zum Zurücksetzen des Passworts, Einladungen zu Arbeitseinsätzen, Hinweise auf offene Bestätigungen im Schießbuch und Erinnerungen an ablaufende Fristen. Gelegentlich informieren wir alle Nutzer über wichtige Änderungen am Dienst. Werbung für Dritte verschicken wir nicht.'
      ]
    },
    {
      id: 'ably',
      title: 'Echtzeit-Aktualisierung (Ably)',
      body: [
        'Damit sich Ansichten wie „Heute“ oder die Schießleiter-Übersicht ohne Neuladen aktualisieren, nutzen wir den Dienst Ably der Ably Realtime Ltd. aus dem Vereinigten Königreich. Öffnest du eine dieser Ansichten, baut dein Browser eine Verbindung zu Ably auf. Dabei werden deine IP-Adresse, eine technische Kennung der Verbindung und die Nachrichten übertragen, die über diese Verbindung laufen, etwa dass es einen neuen Eintrag gibt.',
        `Rechtsgrundlage ist unser berechtigtes Interesse an einer aktuellen Anzeige (Art. 6 Abs. 1 lit. f DSGVO). Für das Vereinigte Königreich gibt es einen Angemessenheitsbeschluss der EU-Kommission. Soweit Ably Daten in weiteren Ländern verarbeitet, geschieht das auf Grundlage von Standardvertragsklauseln. Mehr dazu: ${extLink('https://ably.com/privacy', 'ably.com/privacy')}`
      ]
    },
    {
      id: 'sentry',
      title: 'Fehleranalyse (Sentry)',
      body: [
        'Tritt in der Anwendung ein Fehler auf, schicken wir einen Fehlerbericht an Sentry, einen Dienst der Functional Software, Inc., 45 Fremont Street, 8th Floor, San Francisco, CA 94105, USA. Die Daten werden in der EU-Region von Sentry in Frankfurt am Main gespeichert.',
        // PRÜFEN: In Sentry unter Settings → Security & Privacy „Prevent Storing of IP Addresses“ aktivieren,
        // sonst leitet Sentry die IP serverseitig aus der Verbindung ab.
        'Ein Fehlerbericht enthält die Fehlermeldung, die aufgerufene Seite, Browser, Betriebssystem und den Zeitpunkt. Deine IP-Adresse und Angaben zu deinem Konto werden dabei nicht gespeichert. Bei einem Teil der Seitenaufrufe messen wir außerdem Ladezeiten, um die Anwendung schneller zu machen.',
        `Rechtsgrundlage ist unser berechtigtes Interesse an einer fehlerfreien Anwendung (Art. 6 Abs. 1 lit. f DSGVO). Weil Sentry ein US-Unternehmen ist, lässt sich ein Zugriff aus den USA nicht ausschließen. Sentry ist nach dem EU-US Data Privacy Framework zertifiziert. Mehr dazu: ${extLink('https://sentry.io/privacy/', 'sentry.io/privacy')}`
      ]
    },
    {
      id: 'hcaptcha',
      title: 'Spam-Schutz bei der Registrierung (hCaptcha)',
      body: [
        'Auf der Registrierungsseite nutzen wir hCaptcha der Intuition Machines, Inc. (USA), um automatisierte Anmeldungen zu verhindern. hCaptcha wird nur auf dieser Seite geladen. Dabei werden deine IP-Adresse, Angaben zu Browser und Gerät und dein Verhalten beim Lösen der Aufgabe an hCaptcha übertragen.',
        `Rechtsgrundlage ist unser berechtigtes Interesse, die Anwendung vor Missbrauch zu schützen (Art. 6 Abs. 1 lit. f DSGVO). Die Übermittlung in die USA stützt sich auf das EU-US Data Privacy Framework beziehungsweise auf Standardvertragsklauseln. Mehr dazu: ${extLink('https://www.hcaptcha.com/privacy', 'hcaptcha.com/privacy')}`
      ]
    },
    {
      id: 'browser',
      title: 'Speicherung im Browser, keine Tracking-Cookies',
      body: [
        'schuetzenbuch.de setzt keine Cookies zu Analyse- oder Werbezwecken. Damit du angemeldet bleibst, legen wir im lokalen Speicher deines Browsers (localStorage) Folgendes ab:',
        {
          list: [
            'dein Anmelde-Token und einen Schlüssel, mit dem die Anmeldung bis zu 30 Tage lang verlängert wird,',
            'den Zustand der Anwendung, zum Beispiel deine Profildaten und Einstellungen,',
            'ob du den Hinweis zur Speicherung schon geschlossen hast,',
            'bei Vereinen die Einstellungen für die Monitor-Ansicht „Heute“.'
          ]
        },
        'Diese Speicherung ist technisch notwendig (§ 25 Abs. 2 Nr. 2 TDDDG). Beim Abmelden löschen wir das Anmelde-Token und den Schlüssel. Den gesamten Speicher kannst du jederzeit über die Einstellungen deines Browsers leeren.'
      ]
    },
    {
      id: 'kontakt',
      title: 'Kontakt und Feedback',
      body: [
        'Schreibst du uns per E-Mail oder über das Feedback-Formular, speichern wir deinen Namen, deine E-Mail-Adresse und deine Nachricht, um dein Anliegen zu bearbeiten (Art. 6 Abs. 1 lit. b beziehungsweise f DSGVO).'
      ]
    },
    {
      id: 'empfaenger',
      title: 'Wer deine Daten bekommt',
      body: [
        'Innerhalb von schuetzenbuch.de sind deine Daten für dich und für die Vereine sichtbar, denen du dich zugeordnet hast. Darüber hinaus geben wir Daten nur an die hier genannten Dienstleister weiter, und zwar nur in dem beschriebenen Umfang. Behörden erhalten Daten nur, wenn wir gesetzlich dazu verpflichtet sind.'
      ]
    },
    {
      id: 'speicherdauer',
      title: 'Wie lange wir Daten speichern',
      body: [
        'Wir speichern deine Daten, solange dein Konto besteht. Anmelde-Token löschen wir automatisch, sobald sie abgelaufen sind.',
        // PRÜFEN: Tatsächlichen Löschprozess beschreiben (derzeit Soft-Delete im Backend).
        `Möchtest du dein Konto löschen, schreib uns an ${mailLink}. Wir deaktivieren das Konto dann und löschen die Daten, soweit keine gesetzlichen Aufbewahrungspflichten entgegenstehen. Einträge, die ein Verein für seine Unterlagen braucht, zum Beispiel bestätigte Schießbuch-Einträge, können beim Verein verbleiben.`
      ]
    },
    {
      id: 'rechte',
      title: 'Deine Rechte',
      body: [
        'Du hast das Recht auf:',
        {
          list: [
            'Auskunft über deine gespeicherten Daten (Art. 15 DSGVO),',
            'Berichtigung falscher Daten (Art. 16 DSGVO),',
            'Löschung (Art. 17 DSGVO),',
            'Einschränkung der Verarbeitung (Art. 18 DSGVO),',
            'Datenübertragbarkeit (Art. 20 DSGVO). Einen Großteil deiner Daten kannst du selbst als PDF oder Excel exportieren,',
            'Beschwerde bei einer Datenschutz-Aufsichtsbehörde (Art. 77 DSGVO).'
          ]
        },
        '<strong>Widerspruchsrecht:</strong> Soweit wir Daten auf Grundlage berechtigter Interessen verarbeiten (Art. 6 Abs. 1 lit. f DSGVO), kannst du dieser Verarbeitung aus Gründen, die sich aus deiner besonderen Situation ergeben, jederzeit widersprechen (Art. 21 DSGVO).',
        `Für uns zuständig ist der Landesbeauftragte für den Datenschutz und die Informationsfreiheit Rheinland-Pfalz, Hintere Bleiche 34, 55116 Mainz, ${extLink('https://www.datenschutz.rlp.de', 'datenschutz.rlp.de')}.`
      ]
    }
  ]
};

module.exports = { site, landing, impressum, datenschutz };
